import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "../api/apiClient";   // ✅ GLOBAL CLIENT
import "../css/AddBankDetails.css";
import "../css/mainhome.css";
import { toast } from "react-toastify";
import AppHeader from "../components/AppHeader";
import { useAppContext } from "../context/AppContext";

export default function AddBankDetailsScreen() {
  const navigate = useNavigate();
  const { user, refetchUser } = useAppContext();
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingBank, setIsLoadingBank] = useState(true);
  const [savedBank, setSavedBank] = useState(null);

  const [form, setForm] = useState({
    holder: "",
    accNo: "",
    ifsc: "",
    bank: "",
  });

  const walletBalance = localStorage.getItem("walletBalance");

  const onChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const getRegisterId = () =>
    localStorage.getItem("registerId") ||
    user?.registerId ||
    "";

  const maskAccountNumber = (accountNumber = "") => {
    const rawAccount = String(accountNumber).trim();
    if (rawAccount.length <= 4) return rawAccount;
    return `${"*".repeat(Math.max(rawAccount.length - 4, 4))}${rawAccount.slice(-4)}`;
  };

  const fillFormFromBank = (bankInfo) => {
    setForm({
      holder: bankInfo.accountHolderName || "",
      accNo: bankInfo.accountNumber || "",
      ifsc: bankInfo.ifscCode || "",
      bank: bankInfo.bankName || "",
    });
  };

  const fetchBankDetails = async () => {
    const registerId = String(getRegisterId()).trim();

    if (!registerId) {
      setIsLoadingBank(false);
      return;
    }

    try {
      setIsLoadingBank(true);
      const res = await apiClient.post("/user-bank-details", { registerId });
      const bankInfo = res.data?.info;

      if (res.data?.status === true && bankInfo?.bankStatus) {
        setSavedBank(bankInfo);
        fillFormFromBank(bankInfo);
      } else {
        setSavedBank(null);
      }
    } catch (error) {
      console.error("Error loading bank details:", error?.response || error);
      toast.error(error?.response?.data?.msg || "Failed to load bank details.");
    } finally {
      setIsLoadingBank(false);
    }
  };

  useEffect(() => {
    fetchBankDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    if (isSaving) return;

    const registerId = String(getRegisterId()).trim();

    if (!registerId) {
      toast.error("Please login again before adding bank details.");
      navigate("/login-with-mpin", { replace: true });
      return;
    }

    if (!form.holder.trim() || !form.accNo.trim() || !form.bank.trim() || !form.ifsc.trim()) {
      toast.error("Please fill all required bank details.");
      return;
    }

    try {
      setIsSaving(true);

      const payload = {
        registerId,
        accountHolderName: form.holder.trim(),
        accountNumber: form.accNo.trim(),
        bankName: form.bank.trim(),
        ifscCode: form.ifsc.trim().toUpperCase(),
      };

      const res = await apiClient.post("/update-bank-details", payload);

      if (res.data.status === true) {
        toast.success("Bank details added successfully.");
        await fetchBankDetails();
        if (refetchUser) await refetchUser();
      } else {
        toast.error(res.data.msg || "Failed to add bank details.");
      }
    } catch (error) {
      console.error("Error adding bank details:", error?.response || error);
      toast.error(error?.response?.data?.msg || "Failed to add bank details.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="mainhome-screen-wrapper">
      <div className="add-bank-page">
        <AppHeader
          title="Add Bank Details"
          walletBalance={walletBalance}
          onBack={() => navigate(-1)}
          onWalletClick={() => navigate("/passbook")}
        />

        <div className="add-bank-body">
          <h2 className="add-bank-title">Bank Details</h2>

          <section className="saved-bank-card">
            <div className="saved-bank-card-header">
              <span>Saved Bank Account</span>
              <strong className={savedBank ? "status-added" : "status-empty"}>
                {savedBank ? "ADDED" : "NOT ADDED"}
              </strong>
            </div>

            {isLoadingBank ? (
              <p className="saved-bank-empty">Loading bank details...</p>
            ) : savedBank ? (
              <div className="saved-bank-grid">
                <div>
                  <span>Bank Name</span>
                  <strong>{savedBank.bankName}</strong>
                </div>
                <div>
                  <span>Account Holder</span>
                  <strong>{savedBank.accountHolderName}</strong>
                </div>
                <div>
                  <span>Account Number</span>
                  <strong>{maskAccountNumber(savedBank.accountNumber)}</strong>
                </div>
                <div>
                  <span>IFSC Code</span>
                  <strong>{savedBank.ifscCode}</strong>
                </div>
              </div>
            ) : (
              <p className="saved-bank-empty">No bank account added yet.</p>
            )}
          </section>

          <form
            className="bank-form"
            onSubmit={(e) => {
              e.preventDefault();
              handleSave();
            }}
          >
            <Input
              name="holder"
              placeholder="Account Holder Name"
              value={form.holder}
              onChange={onChange}
            />

            <Input
              name="accNo"
              placeholder="Account Number"
              value={form.accNo}
              onChange={onChange}
            />

            <Input
              name="bank"
              placeholder="Bank Name"
              value={form.bank}
              onChange={onChange}
            />

            <Input
              name="ifsc"
              placeholder="IFSC CODE"
              value={form.ifsc}
              onChange={onChange}
            />

            <button type="submit" className="save-btn" disabled={isSaving}>
              {isSaving ? "SAVING..." : savedBank ? "UPDATE BANK DETAILS" : "SAVE BANK DETAILS"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

/* ——— Tiny controlled input component ——— */
function Input({ icon, ...rest }) {
  return (
    <div className="input-wrap">
      {icon && (
        <div className="input-icon">
          <img src={icon} alt="" />
        </div>
      )}
      <input {...rest} />
    </div>
  );
}
